import { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// ── Colour palette (matches CompareBar palette) ───────────────────────────────
const SWATCHES = [
  '#FF9600', '#9D65C9', '#2196F3', '#E11D74',
  '#22C55E', '#F5A623', '#EF5350', '#26A69A',
  '#F48FB1', '#80DEEA', '#FFEB3B', '#CE93D8',
];

interface Props {
  value: string;
  onChange: (c: string) => void;
  align?: 'left' | 'right';
}

export function ColorPicker({ value, onChange, align = 'right' }: Props) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 184; // 11.5rem ≈ 184px
    const popoverHeight = 120; // approximate
    const gap = 8;

    let left = align === 'left'
      ? rect.left
      : rect.right - popoverWidth;

    // Keep inside viewport horizontally
    if (left < 8) left = 8;
    if (left + popoverWidth > window.innerWidth - 8) {
      left = window.innerWidth - popoverWidth - 8;
    }

    let top = rect.bottom + gap;
    // Flip upward if not enough room below
    if (top + popoverHeight > window.innerHeight - 8) {
      top = rect.top - popoverHeight - gap;
    }

    setPos({ top, left });
  }, [open, align]);

  const popover = (
    <>
      {/* Backdrop — closes popover on outside click */}
      <div
        className="fixed inset-0 z-floating-backdrop"
        onClick={() => setOpen(false)}
      />
      {/* Swatch popover */}
      <div
        className="fixed z-floating-popover bg-modal-bg border border-border rounded-xl shadow-2xl p-3 grid grid-cols-6 gap-2 w-[11.5rem]"
        style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
      >
        {SWATCHES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { onChange(s); setOpen(false); }}
            className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
              value === s ? 'border-white scale-110' : 'border-transparent'
            }`}
            style={{ backgroundColor: s }}
          />
        ))}
        {/* Custom colour input */}
        <button
          type="button"
          title="Custom colour"
          onClick={() => inputRef.current?.click()}
          className="w-6 h-6 rounded-full border-2 border-dashed border-border overflow-hidden relative hover:scale-110 transition-transform flex items-center justify-center"
          style={{ backgroundColor: SWATCHES.includes(value) ? '#555' : value }}
        >
          <span className="text-[8px] text-white font-bold select-none">+</span>
          <input
            ref={inputRef}
            type="color"
            value={value}
            onChange={(e) => { onChange(e.target.value); setOpen(false); }}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            tabIndex={-1}
          />
        </button>
      </div>
    </>
  );

  return (
    <div className="relative">
      {/* Single colour pill */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-7 h-7 rounded-full border-2 border-white/25 shadow-sm hover:scale-110 transition-transform"
        style={{ backgroundColor: value }}
        title="Change colour"
      />

      {open && pos && createPortal(popover, document.body)}
    </div>
  );
}
